import React, { useState } from 'react';
import { Modal } from './ui/Modal.jsx';
import { AmountStep } from './steps/AmountStep.jsx';
import { UpsellStep } from './steps/UpsellStep.jsx';
import { CheckoutSteps, buildStepProps } from './steps/CheckoutSteps.jsx';
import { buildSteps } from './flow/steps.js';
import { useAcquisitionFlow } from './hooks/useAcquisitionFlow.js';
import { buildThemeVars } from './utils/theme.js';

/**
 * Default root: the acquisition form as an inline block or behind a button that
 * opens a modal. The donor picks an amount and frequency, then goes through the
 * shared checkout steps.
 */
export function AcquisitionForm({ config, display, triggerElement, onClose, isPreview = false }) {
  const isInline = display === 'Inline';
  const [isOpen, setIsOpen] = useState(isInline);

  const flow = useAcquisitionFlow({ config, steps: buildSteps(config), isPreview });
  const { currentStep } = flow;

  function handleClose() {
    if (isInline) return;
    setIsOpen(false);
    setTimeout(flow.reset, 300);
    onClose?.();
  }

  function handleOpen() {
    flow.reset();
    setIsOpen(true);
  }

  function handleUpsellAccept(monthlyAmount) {
    flow.handleChange({
      frequencyId: 'Monthly',
      amount: monthlyAmount,
      isCustomAmount: false,
      customAmount: '',
      upsellAccepted: true
    });
    flow.handleNext();
  }

  function handleUpsellDecline() {
    flow.handleChange({ upsellAccepted: false });
    flow.handleNext();
  }

  const stepProps = buildStepProps({ config, flow, isInline, isPreview, onClose: handleClose });

  const stepContent = (
    <>
      {currentStep === 'amount' && (
        <AmountStep {...stepProps} />
      )}
      {currentStep === 'upsell' && (
        <UpsellStep
          config={config}
          formData={flow.formData}
          onAccept={handleUpsellAccept}
          onDecline={handleUpsellDecline}
          onBack={flow.handleBack}
          onClose={handleClose}
          isInline={isInline}
        />
      )}
      <CheckoutSteps
        config={config}
        flow={flow}
        isInline={isInline}
        isPreview={isPreview}
        onClose={handleClose}
      />
    </>
  );

  const themeVars = buildThemeVars(config.Visual);

  if (display === 'ButtonModal') {
    const triggerLabel = triggerElement?.textContent?.trim() || 'Doe agora';

    return (
      <>
        <button
          type="button"
          className="tdw-trigger"
          style={themeVars}
          onClick={handleOpen}
        >
          {triggerLabel}
        </button>

        <Modal
          isOpen={isOpen}
          onClose={handleClose}
          config={config}
          themeVars={themeVars}
        >
          {stepContent}
        </Modal>
      </>
    );
  }

  // Inline mode — right panel content only, no left panel
  return (
    <div className="tdw-inline" style={themeVars}>
      <div className="tdw-panel-right">
        {stepContent}
      </div>
    </div>
  );
}
